import { IndexedDBService } from './IndexedDBService';
import type { FinancialStatementType } from '../../types/reports';
import type { CurrencyCode } from '../../config/currency';

export interface GeneratedReport {
  id: string;
  type: FinancialStatementType;
  title: string;
  data: any;
  currency: CurrencyCode;
  generatedAt: string;
  generatedBy: string;
  organization: {
    name: string;
    registrationNumber: string;
  };
  isAudited: boolean;
  format: 'pdf' | 'excel' | 'json';
}

export class ReportsStorageService {
  private static STORE_NAME = 'generated_reports';

  /**
   * Sauvegarde un rapport généré dans IndexedDB
   */
  static async saveReport(report: Omit<GeneratedReport, 'id' | 'generatedAt'>): Promise<GeneratedReport> {
    try {
      console.log('📦 ReportsStorageService.saveReport called with:', { type: report.type, title: report.title });
      
      const newReport: GeneratedReport = {
        ...report,
        id: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        generatedAt: new Date().toISOString()
      };

      console.log('📦 Saving to IndexedDB:', { id: newReport.id, type: newReport.type });
      await IndexedDBService.add(this.STORE_NAME, newReport);
      console.log('📦 Report saved successfully to IndexedDB');
      
      return newReport;
    } catch (error) {
      console.error('❌ Error saving report to IndexedDB:', error);
      throw new Error('Failed to save report');
    }
  }

  /**
   * Récupère tous les rapports générés
   */
  static async getAllReports(): Promise<GeneratedReport[]> {
    try {
      console.log('📦 ReportsStorageService.getAllReports called');
      const reports = await IndexedDBService.getAll<GeneratedReport>(this.STORE_NAME);
      console.log('📦 Retrieved reports from IndexedDB:', { count: reports.length });
      
      // Trier par date de génération (plus récent en premier)
      return reports.sort((a, b) => 
        new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
      );
    } catch (error) {
      console.error('❌ Error getting reports from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Récupère un rapport par son ID
   */
  static async getReport(id: string): Promise<GeneratedReport | null> {
    try {
      const report = await IndexedDBService.get<GeneratedReport>(this.STORE_NAME, id);
      return report || null;
    } catch (error) {
      console.error('Error getting report from IndexedDB:', error);
      return null;
    }
  }

  /**
   * Récupère les rapports par type
   */
  static async getReportsByType(type: FinancialStatementType): Promise<GeneratedReport[]> {
    try {
      const reports = await IndexedDBService.getByIndex<GeneratedReport>(
        this.STORE_NAME,
        'type',
        type
      );
      
      // Trier par date de génération (plus récent en premier)
      return reports.sort((a, b) => 
        new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
      );
    } catch (error) {
      console.error('Error getting reports by type from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Supprime un rapport par son ID
   */
  static async deleteReport(id: string): Promise<void> {
    try {
      await IndexedDBService.delete(this.STORE_NAME, id);
    } catch (error) {
      console.error('Error deleting report from IndexedDB:', error);
      throw new Error('Failed to delete report');
    }
  }

  /**
   * Supprime tous les rapports
   */
  static async clearAllReports(): Promise<void> {
    try {
      await IndexedDBService.clear(this.STORE_NAME);
    } catch (error) {
      console.error('Error clearing reports from IndexedDB:', error);
      throw new Error('Failed to clear reports');
    }
  }

  /**
   * Compte le nombre total de rapports
   */
  static async countReports(): Promise<number> {
    try {
      return await IndexedDBService.count(this.STORE_NAME);
    } catch (error) {
      console.error('Error counting reports from IndexedDB:', error);
      return 0;
    }
  }
}
